import { formatDate } from '@/shared/utils/dateUtils';

import type { ExecutionBucket, VerificationBucket } from '../types/testAccuracy';

export function toYmd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function formatDisplayDate(iso?: string | null): string {
  if (!iso) {
    return '—';
  }
  const [y, m, d] = iso.split('T')[0].split('-');
  if (!y || !m || !d) {
    return iso;
  }
  const formatted = formatDate(new Date(Number(y), Number(m) - 1, Number(d)));
  return formatted || iso;
}

export function periodDates(
  period: '7' | '30' | 'custom',
  customFrom?: string,
  customTo?: string
): { from: string; to: string } {
  if (period === 'custom') {
    return { from: customFrom || '', to: customTo || '' };
  }
  const today = new Date();
  const to = toYmd(today);
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - (Number(period) - 1));
  return { from: toYmd(fromDate), to };
}

export function roundPctOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

export function toPct(n: number, total: number): number | null {
  if (total <= 0) {
    return null;
  }
  return roundPctOneDecimal((n / total) * 100);
}

/** Prefer efficiency_pct from backend, fall back to pass_rate. */
export function getVerificationPassRate(metrics?: {
  efficiency_pct?: number | null;
  pass_rate?: number | null;
}): number | null {
  if (!metrics) {
    return null;
  }
  if (metrics.efficiency_pct !== null && metrics.efficiency_pct !== undefined) {
    return metrics.efficiency_pct;
  }
  if (metrics.pass_rate !== null && metrics.pass_rate !== undefined) {
    return metrics.pass_rate;
  }
  return null;
}

export function formatRate(rate?: number | null): string {
  if (rate === null || rate === undefined) {
    return '—';
  }
  return `${roundPctOneDecimal(rate)}%`;
}

export function rateTone(rate?: number | null): 'pass' | 'warn' | 'fail' | 'muted' {
  if (rate === null || rate === undefined) {
    return 'muted';
  }
  if (rate >= 75) {
    return 'pass';
  }
  if (rate >= 40) {
    return 'warn';
  }
  return 'fail';
}

export function rateToneClass(tone: 'pass' | 'warn' | 'fail' | 'muted'): string {
  if (tone === 'pass') {
    return 'text-emerald-700';
  }
  if (tone === 'warn') {
    return 'text-amber-700';
  }
  if (tone === 'fail') {
    return 'text-red-700';
  }
  return 'text-gray-500';
}

export function rateBarClass(tone: 'pass' | 'warn' | 'fail' | 'muted'): string {
  if (tone === 'pass') {
    return 'bg-emerald-500';
  }
  if (tone === 'warn') {
    return 'bg-amber-500';
  }
  if (tone === 'fail') {
    return 'bg-red-500';
  }
  return 'bg-gray-300';
}

export function verificationBadge(bucket?: string): { label: string; className: string } {
  const map: Record<VerificationBucket, { label: string; className: string }> = {
    passed_1st_attempt: {
      label: '1st ✓',
      className: 'bg-emerald-100 text-emerald-800',
    },
    passed_2nd_attempt: {
      label: '2nd ✓',
      className: 'bg-emerald-100 text-emerald-800',
    },
    passed_3rd_plus_attempt: {
      label: '3rd+ ✓',
      className: 'bg-yellow-100 text-yellow-800',
    },
    not_passed: {
      label: 'Never Passed',
      className: 'bg-red-100 text-red-800',
    },
  };
  return (
    map[bucket as VerificationBucket] || {
      label: '—',
      className: 'bg-gray-100 text-gray-600',
    }
  );
}

export function executionBadge(bucket?: string): { label: string; className: string } {
  const map: Record<ExecutionBucket, { label: string; className: string }> = {
    passed_after_first_verify: {
      label: 'Passed',
      className: 'bg-emerald-100 text-emerald-800',
    },
    required_reverification: {
      label: 'Re-verify',
      className: 'bg-emerald-100 text-emerald-800',
    },
    never_passed_after_verify: {
      label: 'Never',
      className: 'bg-red-100 text-red-800',
    },
    regressed: {
      label: 'Regressed',
      className: 'bg-red-100 text-red-800 border border-red-200',
    },
  };
  return (
    map[bucket as ExecutionBucket] || {
      label: '—',
      className: 'bg-gray-100 text-gray-600',
    }
  );
}
